import { useQuery } from '@tanstack/react-query';
import { useLocale } from './useLocale';
import { translations } from './translations';
import type { Translations, Locale } from './translations';
import { configApi } from '@/api/client';

type TranslationKey = keyof Translations;

/**
 * Fetch translation overrides from the API.
 * Returns empty object if not configured or on error.
 */
async function fetchTranslationOverrides(locale: Locale): Promise<Record<string, string>> {
  const localeKey = locale === 'fi-FI' ? 'fi' : 'en';
  return configApi.getTranslations(localeKey);
}

export function useTranslation() {
  const { locale } = useLocale();
  const defaultTranslations = translations[locale];

  // Fetch translation overrides from API
  const { data: overrides } = useQuery({
    queryKey: ['config', 'translations', locale],
    queryFn: () => fetchTranslationOverrides(locale),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  /**
   * Get a translated string by key.
   * Priority: API override > default translation > key itself
   * Supports template variables like {{name}} in translations.
   */
  function translate(key: TranslationKey | string, params?: Record<string, string | number>): string {
    let text: string;

    // Check API overrides first
    if (overrides && key in overrides) {
      text = overrides[key];
    } else if (key in defaultTranslations) {
      text = defaultTranslations[key as TranslationKey];
    } else {
      // Return the key if not found (helps identify missing translations)
      return key;
    }

    // Replace template variables if params provided
    if (params) {
      for (const [paramKey, value] of Object.entries(params)) {
        text = text.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(value));
      }
    }

    return text;
  }

  return { t: translate, locale };
}

/**
 * Format a date to locale time string (HH:MM).
 */
export function formatTime(date: Date | string, locale: string = 'fi-FI'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Get the correct plural form for game count.
 */
export function getCountTextKey(count: number): 'gamesCount' | 'gamesCountSingle' {
  return count === 1 ? 'gamesCountSingle' : 'gamesCount';
}
